import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router';

import { Icon, focusRingOnDark, type IconName } from '@/design-system';
import { cn } from '@/shared/lib';

export interface HeaderTab {
    /** Uniek in de rij. */
    key: string;
    label: string;
    /** Het adres van het tabblad: elk tabblad is een eigen pagina. */
    to: string;
    /** Het lijn-icoon voor het label. */
    icon: IconName;
}

export interface HeaderTabsProps {
    /** De naam van de rij voor een schermlezer: "Keuring", "Instellingen". */
    label: string;
    tabs: readonly HeaderTab[];
    /** Alleen de afstand tot wat erboven staat; standaard 8px. */
    className?: string;
}

/**
 * De tabbladen onderaan een donkere kop: die van een keuring, en die van
 * Instellingen.
 *
 * Het zijn links, geen ARIA-tabs: elk tabblad is een eigen adres, en `NavLink`
 * zet `aria-current="page"` op het tabblad dat openstaat. Dat tabblad heeft
 * witte tekst en dezelfde accentstaaf van 2px als het actieve item in de rail,
 * hier onder het label.
 *
 * Elk tabblad is minstens 44px hoog. Past de rij niet, dan schuift hij
 * horizontaal - nooit afgesneden - en het open tabblad schuift in beeld. De
 * focusring staat binnen het tabblad, zodat de schuifrand hem niet afsnijdt.
 *
 * De rij loopt over de volle breedte van een kop met `px-7`, zodat het schuiven
 * tot de rand loopt; het eerste label lijnt uit met de tekst erboven.
 */
export function HeaderTabs({ label, tabs, className }: HeaderTabsProps) {
    const list = useRef<HTMLUListElement>(null);
    const { pathname } = useLocation();

    useEffect(() => {
        list.current
            ?.querySelector<HTMLElement>('[aria-current="page"]')
            ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }, [pathname, tabs.length]);

    return (
        <nav aria-label={label} className={cn('-mx-7 mt-2 min-w-0', className)}>
            <ul ref={list} className="m-0 flex list-none gap-1 overflow-x-auto px-4 py-0">
                {tabs.map((tab) => (
                    <li key={tab.key} className="shrink-0">
                        <NavLink
                            to={tab.to}
                            end
                            className={({ isActive }) =>
                                cn(
                                    'relative inline-flex min-h-[var(--control-h-md)] items-center gap-2 px-3 whitespace-nowrap no-underline',
                                    'rounded-t-[var(--radius-control)] font-[family-name:var(--font-body)] text-[14px] leading-[1.25]',
                                    isActive
                                        ? 'font-medium text-[var(--text-on-dark)]'
                                        : 'text-[var(--text-on-dark-3)] hover:bg-[var(--ink-hover-on-dark)] hover:text-[var(--text-on-dark)]',
                                    focusRingOnDark,
                                    'focus-visible:outline-offset-[-2px]',
                                )
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <Icon name={tab.icon} size="sm" />
                                    {tab.label}
                                    {isActive ? (
                                        <span
                                            aria-hidden="true"
                                            className="absolute inset-x-3 bottom-0 h-[var(--border-width-active)] rounded-[2px] bg-[var(--accent)]"
                                        />
                                    ) : null}
                                </>
                            )}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
